import React from 'react';
import { Link } from 'react-router-dom';
import { Mic, MicOff, PhoneCall } from 'lucide-react';
import { fmtPhone } from '../../lib/format.js';

const TONE = {
  'in-progress': { box: 'bg-green-500/10 border-green-500/30', text: 'text-green-400', label: 'Connected' },
  ringing: { box: 'bg-yellow-500/10 border-yellow-500/30', text: 'text-yellow-400', label: 'Ringing...' },
  initiating: { box: 'bg-civix-purple/10 border-civix-purple/30', text: 'text-civix-purpleSoft', label: 'Connecting...' },
};

/**
 * In-call status: pulsing "Ringing..." pill, who is being called (lead name
 * linked to the profile, else the formatted number), timer, and mute toggle.
 * @param {Object} props
 * @param {'initiating'|'ringing'|'in-progress'} props.callState
 * @param {{leadName?: string, leadId?: string, fromNumber?: string, toNumber?: string}|null} props.activeLead
 * @param {boolean} props.muted
 * @param {number} props.callDuration - Seconds connected.
 * @param {(sec: number) => string} props.formatDuration
 * @param {() => void} props.onMute
 * @returns {JSX.Element}
 */
export default function CallStatusCard({ callState, activeLead, muted, callDuration, formatDuration, onMute }) {
  const tone = TONE[callState] || TONE.initiating;
  const ringing = callState === 'ringing';
  const name = activeLead?.leadName;
  const number = activeLead?.toNumber || activeLead?.fromNumber;

  return (
    <div className={`rounded-xl px-4 py-3 border space-y-2 ${tone.box}`} data-testid="call-status-card">
      <div className="flex items-center justify-between gap-2">
        <span
          className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider ${tone.text} ${ringing ? 'animate-pulse' : ''}`}
          data-testid="call-status-pill"
        >
          {ringing && <PhoneCall className="w-3.5 h-3.5" />}
          {tone.label}
        </span>
        {callState === 'in-progress' && <span className="text-white font-mono text-sm">{formatDuration(callDuration)}</span>}
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          {name ? (
            <>
              {activeLead?.leadId ? (
                <Link
                  to={`/leads/${activeLead.leadId}`}
                  className="text-white text-sm font-semibold truncate block hover:text-civix-cyan underline-offset-2 hover:underline"
                  title={`Open ${name}`}
                >
                  {name}
                </Link>
              ) : (
                <div className="text-white text-sm font-semibold truncate" title={name}>{name}</div>
              )}
              {number && <div className="text-white/50 text-xs font-mono">{fmtPhone(number)}</div>}
            </>
          ) : (
            number && <div className="text-white text-sm font-semibold font-mono">{fmtPhone(number)}</div>
          )}
        </div>

        <button
          onClick={onMute}
          aria-pressed={muted}
          className={`shrink-0 flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs font-semibold transition ${
            muted ? 'bg-red-500/30 text-red-300 border border-red-500/40' : 'bg-civix-panelLight text-white/70 hover:text-white border border-civix-border'
          }`}
          title={muted ? 'Unmute microphone' : 'Mute microphone'}
        >
          {muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          {muted ? 'Unmute' : 'Mute'}
        </button>
      </div>
    </div>
  );
}
